'use client'

import { AlertTriangle } from 'lucide-react'

import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

interface MultiOverlapWarningModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	overlapCount: number
	onContinue: () => void
	onBack: () => void
}

export const MultiOverlapWarningModal = ({
	open,
	onOpenChange,
	overlapCount,
	onContinue,
	onBack,
}: MultiOverlapWarningModalProps) => {
	const { t } = useTranslation()

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-lg p-0 gap-0">
				<DialogHeader className="px-6 pt-6 pb-4">
					<div className="flex items-center gap-3">
						<div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
							<AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
						</div>
						<div>
							<DialogTitle className="text-lg">
								{t('entities.schedule.calendar.multiple_overlaps_detected')}
							</DialogTitle>
							<DialogDescription className="text-sm mt-1">
								{t('entities.schedule.calendar.overlap_description')}
								{typeof overlapCount === 'number' && overlapCount > 0
									? ` (${overlapCount}).`
									: '.'}
							</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<Separator />

				<div className="px-6 py-5 space-y-3 text-sm text-muted-foreground">
					<p>{t('entities.schedule.overlap.what_will_happen')}</p>
					<p>{t('entities.schedule.calendar.overlap_consequence')}</p>
				</div>

				<Separator />

				<DialogFooter className="px-6 py-4">
					<Button variant="outline" onClick={onBack}>
						{t('actions.back')}
					</Button>
					<Button onClick={onContinue}>{t('actions.continue')}</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
